// src/components/WishlistButton.tsx
import React, { useState } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useWishlist } from '../hooks/useWishlist';
import { supabase } from '../services/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface WishlistButtonProps {
    variantId: number;
    className?: string;
    size?: 'small' | 'medium' | 'large';
}

export const WishlistButton: React.FC<WishlistButtonProps> = ({
                                                                  variantId,
                                                                  className = '',
                                                                  size = 'medium'
                                                              }) => {
    const navigate = useNavigate();
    const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
    const [isLoading, setIsLoading] = useState(false);

    const isActive = isInWishlist(variantId);

    const handleClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isLoading) return;

        // Проверяем авторизацию
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
            toast.error('Войдите в аккаунт, чтобы добавить в избранное');
            // Сохраняем текущий путь для редиректа после логина
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
            navigate('/login');
            return;
        }

        setIsLoading(true);

        try {
            if (isActive) {
                await removeFromWishlist(variantId);
            } else {
                await addToWishlist(variantId);
            }
        } catch (error) {
            console.error('Error toggling wishlist:', error);
            toast.error('Ошибка. Попробуйте позже.');
        } finally {
            setIsLoading(false);
        }
    };

    const sizeClasses = {
        small: { width: '18px', height: '18px' },
        medium: { width: '22px', height: '22px' },
        large: { width: '26px', height: '26px' }
    };

    const buttonSizes = {
        small: { width: '32px', height: '32px' },
        medium: { width: '40px', height: '40px' },
        large: { width: '48px', height: '48px' }
    };

    return (
        <button
            onClick={handleClick}
            className={`wishlist-btn ${className}`}
            aria-label={isActive ? 'Удалить из избранного' : 'Добавить в избранное'}
            disabled={isLoading}
            style={{
                background: 'white',
                border: 'none',
                borderRadius: '50%',
                width: buttonSizes[size].width,
                height: buttonSizes[size].height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isLoading ? 'wait' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                opacity: isLoading ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
                if (!isLoading) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
        >
            {isActive ? (
                <HeartIconSolid
                    style={{
                        width: sizeClasses[size].width,
                        height: sizeClasses[size].height,
                        color: '#ef4444',
                        fill: '#ef4444'
                    }}
                />
            ) : (
                <HeartIcon
                    style={{
                        width: sizeClasses[size].width,
                        height: sizeClasses[size].height,
                        color: '#9ca3af',
                    }}
                />
            )}
        </button>
    );
};